import { MoneyV2 } from '@/__generated__/storefront/graphql';

export const MONEY_FORMAT_PLACEHOLDERS = {
  AMOUNT: 'amount',
  AMOUNT_NO_DECIMALS: 'amount_no_decimals',
  AMOUNT_WITH_COMMA_SEPARATOR: 'amount_with_comma_separator',
  AMOUNT_NO_DECIMALS_WITH_COMMA_SEPARATOR:
    'amount_no_decimals_with_comma_separator',
  AMOUNT_WITH_APOSTROPHE_SEPARATOR: 'amount_with_apostrophe_separator'
} as const;

export const VALUE_NOT_REACHED_FALLBACK = '-';
const FALLBACK_MONEY_FORMAT = '${{amount}}';
const PLACEHOLDER_REGEX = /\{\{\s*(\w+)\s*\}\}/;
const THOUSAND_SEPARATOR_REGEX = /(\d)(?=(\d{3})+(?!\d))/g;

/**
 * Apply currency-specific rounding logic
 * if a currency code requires rounding, round the amount
 * thats essentially all currencies except EUR, GBP and USD
 * some context can be found here:
 * https://hobbii.atlassian.net/wiki/spaces/TECH/pages/2609250306/Price+Calculations
 * https://hobbii.atlassian.net/wiki/spaces/TECH/pages/3082354689/Price+Rounding+Pretty+Prices
 */
const applyCurrencyRounding = (cents: number): number => {
  const currency = window?.Shopify?.currency?.active;

  if (
    currency &&
    currency !== 'EUR' &&
    currency !== 'GBP' &&
    currency !== 'USD' &&
    currency !== 'CAD'
  ) {
    return Math.round(cents / 100) * 100;
  }

  return cents;
};

const applyCADFormatAdjustment = (format?: string): string | undefined => {
  const currency = window?.Shopify?.currency?.active;
  const enablePresentmentCADCurrency =
    window?.Hobbii?.flags?.enablePresentmentCADCurrency;

  if (
    enablePresentmentCADCurrency &&
    currency === 'CAD' &&
    format &&
    format.includes('$')
  ) {
    return format.replace('$', 'CA$ ');
  }

  return format;
};

const formattedPrice = (value: number) => {
  const format = window?.Hobbii?.moneyFormat;
  const roundedValue = applyCurrencyRounding(value);
  return formatMoney(roundedValue, format);
};

/** Like formattedPrice but always shows decimal places (e.g. $10.00 instead of $10), matching Liquid's {{ | money }} filter. */
const formattedPriceWithDecimals = (value: number) => {
  const format = window?.Hobbii?.moneyFormat;
  const roundedValue = applyCurrencyRounding(value);
  return formatMoney(roundedValue, format, true);
};

const formatPriceStorefront = (price?: MoneyV2) => {
  const cents = parseFloat(price?.amount || '0') * 100;
  const format = window?.Hobbii?.moneyFormat;
  const roundedCents = applyCurrencyRounding(cents);
  return formatMoney(roundedCents, format);
};

const formatMoney = (
  cents: number,
  format?: string,
  forceDecimals = false
): string => {
  if (typeof cents !== 'number' || Number.isNaN(cents)) {
    if (import.meta.env.DEV) {
      throw new Error(
        `formatMoney: cents argument not of accepted format ${JSON.stringify(
          cents
        )}, must be a number.`
      );
    }

    return VALUE_NOT_REACHED_FALLBACK;
  }

  const adjustedFormat = applyCADFormatAdjustment(format);

  const formatString = adjustedFormat ?? FALLBACK_MONEY_FORMAT;

  const targetPlaceholderMatch = formatString.match(PLACEHOLDER_REGEX);
  const targetPlaceholder = targetPlaceholderMatch?.[1];

  // NOTE: we could fall back to `Intl.NumberFormat` instead of defaulting to ${{amount}}':
  // new Intl.NumberFormat(window.Hobbii?.storeLocale, { style: 'currency', currency: window.Shopify.currency.active}).format(1000000)
  const formattedValue = targetPlaceholder
    ? formatWithPlaceholder(cents, targetPlaceholder, forceDecimals)
    : formatWithDelimiters(cents, 2, ',', '.', forceDecimals);
  return formatString.replace(PLACEHOLDER_REGEX, formattedValue);
};

const formatWithPlaceholder = (
  cents: number,
  targetPlaceholder: string,
  forceDecimals = false
): string => {
  switch (targetPlaceholder) {
    case MONEY_FORMAT_PLACEHOLDERS.AMOUNT:
      return formatWithDelimiters(cents, 2, ',', '.', forceDecimals);
    case MONEY_FORMAT_PLACEHOLDERS.AMOUNT_NO_DECIMALS:
      return formatWithDelimiters(cents, 0);
    case MONEY_FORMAT_PLACEHOLDERS.AMOUNT_WITH_COMMA_SEPARATOR:
      return formatWithDelimiters(cents, 2, '.', ',', forceDecimals);
    case MONEY_FORMAT_PLACEHOLDERS.AMOUNT_NO_DECIMALS_WITH_COMMA_SEPARATOR:
      return formatWithDelimiters(cents, 0, '.', ',');
    case MONEY_FORMAT_PLACEHOLDERS.AMOUNT_WITH_APOSTROPHE_SEPARATOR:
      return formatWithDelimiters(cents, 2, "'", '.', forceDecimals);
  }

  if (import.meta.env.DEV) {
    throw new Error(
      `formatMoney: No match for placeholder "${targetPlaceholder}" found when formatting "${cents}" cents.`
    );
  }

  // NOTE: default to returning MONEY_FORMAT_PLACEHOLDERS.AMOUNT in prod.
  // This will default to "{{amount}} bananas" for stuff like "{{bananas}} bananas"
  return formatWithDelimiters(cents, 2, ',', '.', forceDecimals);
};

const formatWithDelimiters = (
  cents: number,
  precision = 2,
  thousands = ',',
  decimal = '.',
  forceDecimals = false
): string => {
  const numberStr = (Math.round(cents) / 100).toFixed(precision);
  const [wholePart, decimalPart] = numberStr.split('.');

  const wholePartFormatted = wholePart.replace(
    THOUSAND_SEPARATOR_REGEX,
    `$1${thousands}`
  );

  // TODO
  // NOTE: stripping '.00' for round amounts (e.g. $10.00 → $10) is incorrect for the `amount`
  // placeholder, which by Shopify's own naming convention implies decimals — that is precisely
  // why the no-decimal variant is called `amount_no_decimals`. This behavior diverges from
  // Liquid's `| money` filter and should be removed globally. `forceDecimals` is a workaround
  // introduced to restore the correct behavior in specific places until a proper fix is made.
  const decimalPartFormatted =
    decimalPart && (forceDecimals || decimalPart !== '00')
      ? decimal + decimalPart
      : '';

  return wholePartFormatted + decimalPartFormatted;
};

/**
 * Converts a Shopify compare_at_price value (string or number) to cents.
 * Shopify may return compare_at_price as a decimal string (e.g. "19.99") or
 * as an integer number of cents (e.g. 1999) depending on the API context.
 */
const parseCompareAtPrice = (val: string | number): number =>
  typeof val === 'string'
    ? Math.round(parseFloat(val) * 100)
    : Math.round(val * 100);

const isCompareAtPriceHigher = (
  price: MoneyV2 | null | undefined,
  compareAtPrice: MoneyV2 | null | undefined
): boolean => {
  const priceAmount = parseFloat(price?.amount ?? '');
  const compareAtPriceAmount = parseFloat(compareAtPrice?.amount ?? '');
  if (!priceAmount || !compareAtPriceAmount) return false;
  return compareAtPriceAmount > priceAmount;
};

export {
  applyCurrencyRounding,
  formatPriceStorefront,
  formattedPrice,
  formattedPriceWithDecimals,
  isCompareAtPriceHigher,
  parseCompareAtPrice
};
